import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Phone,
} from "lucide-react";
import apiService from "../services/api";

const ContractSubmissionPage = () => {
  const breadcrumbs = [
    { label: "Home", link: "/" },
    { label: "Submit Contract", link: null },
  ];

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    contractFileName: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (!allowedTypes.includes(file.type)) {
        setError(
          "Please upload a valid file (JPEG, JPG, PNG, PDF, DOC, or DOCX)",
        );
        e.target.value = "";
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        e.target.value = "";
        return;
      }

      setSelectedFile(file);
      setFormData((prev) => ({
        ...prev,
        contractFileName: file.name,
      }));
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await apiService.submitContract({
        fullName: formData.fullName,
        email: formData.email,
        contractFileName: formData.contractFileName,
      });

      setSuccess(true);
      setFormData({
        fullName: "",
        email: "",
        contractFileName: "",
      });
      setSelectedFile(null);

      const fileInput = document.getElementById("contractFile");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      if (err.code === "DUPLICATE_CONTRACT") {
        setError(
          "A contract has already been submitted with this email address.",
        );
      } else {
        setError(err.message || "Failed to submit contract. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section
        className="py-12 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: "#F5F0F1" }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span>|</span>}
                {crumb.link ? (
                  <Link
                    to={crumb.link}
                    className="hover:text-[#E85D2C] transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-gray-900">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4">
              Submit Your Signed Contract
            </h1>

            <p className="text-lg text-gray-700 font-light leading-relaxed mb-6">
              Upload your completed employment contract to finalize your
              onboarding process with Allawah Lodge.
            </p>

            <a
              href="tel:+61269272706"
              className="inline-flex items-center gap-2 bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-medium px-6 py-3 rounded-full transition-colors"
            >
              <Phone size={20} />
              CALL: (02) 6927 2706
            </a>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="mx-4 md:mx-24 bg-white py-8 md:py-16 px-8 md:px-16 rounded-3xl mt-4 md:mt-8 mb-4 md:mb-8">
        <div className="max-w-2xl mx-auto">
          {success && (
            <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle
                size={24}
                className="text-green-600 flex-shrink-0 mt-0.5"
              />
              <div>
                <h3 className="text-lg font-bold text-green-900 mb-2">
                  Contract Submitted Successfully!
                </h3>
                <p className="text-sm text-green-800">
                  Thank you for submitting your contract. You will receive a
                  confirmation email shortly at{" "}
                  {formData.email || "your email address"}.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle
                size={24}
                className="text-red-600 flex-shrink-0 mt-0.5"
              />
              <div>
                <h3 className="text-lg font-bold text-red-900 mb-2">
                  Submission Error
                </h3>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Contract Upload Form
            </h2>
            <p className="text-sm text-gray-600">
              Please provide your details and upload your signed contract
              document
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E85D2C] focus:border-transparent"
                placeholder="Enter your email address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Signed Contract <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Accepted formats: JPEG, JPG, PNG, PDF, DOC, DOCX (Max 10MB)
              </p>
              <div className="relative">
                <input
                  type="file"
                  id="contractFile"
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                  required
                  className="hidden"
                />
                <label
                  htmlFor="contractFile"
                  className="flex items-center justify-center gap-3 w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#E85D2C] transition-colors bg-gray-50"
                >
                  <Upload size={24} className="text-gray-400" />
                  <div className="text-center">
                    {selectedFile ? (
                      <div>
                        <p className="text-sm font-medium text-[#E85D2C]">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          Click to upload contract
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          or drag and drop
                        </p>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading || !selectedFile}
                className="w-full bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-medium py-4 px-8 rounded-full transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FileText size={20} />
                    Submit Contract
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ContractSubmissionPage;
